import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface SidebarUserProfileProps {
  collapsed: boolean;
  nickname: string | null;
  role: string | null;
}

export const SidebarUserProfile: React.FC<SidebarUserProfileProps> = ({ collapsed, nickname, role }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserImage = async () => {
      if (!role) {
        setLoading(false);
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        let userImageUrl = null;

        // Buscar imagem baseada no role
        if (role === 'mentor') {
          console.log('🔍 [SidebarUserProfile] Buscando imagem do mentor para user ID:', user.id);
          const { data: mentorData, error: mentorError } = await supabase
            .from("mentor_info")
            .select("image_url")
            .eq("profile_id", user.id)
            .maybeSingle();

          if (!mentorError && mentorData?.image_url) {
            userImageUrl = mentorData.image_url;
            console.log('✅ [SidebarUserProfile] Imagem do mentor encontrada:', userImageUrl);
            console.log('🔍 [SidebarUserProfile] Verificando se URL é válida...');
          } else {
            console.log('⚠️ [SidebarUserProfile] Imagem do mentor não encontrada ou erro:', mentorError?.message);
            console.log('🔍 [SidebarUserProfile] Dados do mentor retornados:', mentorData);
          }
        } else if (role === 'jogador') {
          console.log('🔍 [SidebarUserProfile] Buscando imagem do jogador para user ID:', user.id);
          const { data: playerDataArray, error: playerError } = await supabase
            .from("player_social_media")
            .select("image_url")
            .eq("profile_id", user.id);

          if (!playerError && playerDataArray && playerDataArray.length > 0) {
            userImageUrl = playerDataArray[0]?.image_url;
            console.log('✅ [SidebarUserProfile] Imagem do jogador encontrada:', userImageUrl);
            console.log('🔍 [SidebarUserProfile] Total de registros encontrados:', playerDataArray.length);
          } else {
            console.log('⚠️ [SidebarUserProfile] Imagem do jogador não encontrada ou erro:', playerError?.message);
            console.log('🔍 [SidebarUserProfile] Dados do jogador retornados:', playerDataArray);
          }
        }
        // Para admin, manter sem imagem por enquanto

        setImageUrl(userImageUrl);
      } catch (error) {
        console.error('❌ [SidebarUserProfile] Erro ao buscar imagem do usuário:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserImage();
  }, [role]);

  if (!role) return null;
  
  return (
    <div className={`px-3 py-4 ${collapsed ? 'items-center' : ''} flex flex-col`}>
      <div className={`flex ${collapsed ? 'justify-center' : 'items-center'} mb-2`}>
        <Avatar className={`${collapsed ? 'h-10 w-10' : 'h-12 w-12'} rounded-full border-2 border-[#00B6F0]`}>
          <AvatarImage 
            src={imageUrl || undefined} 
            alt={nickname || role || 'User'}
            onError={(e) => {
              console.log('❌ [SidebarUserProfile] Erro ao carregar imagem do avatar');
            }}
          />
          <AvatarFallback className="bg-[#223342] text-[#00B6F0]">
            {nickname?.slice(0, 2) || (role?.slice(0, 1).toUpperCase() || 'U')}
          </AvatarFallback>
        </Avatar>
        {!collapsed && (
          <div className="ml-3">
            <p className="text-white font-medium truncate">{nickname || role}</p>
            <div className="text-xs bg-gradient-to-r from-[#00B6F0] to-[#9f00ff] bg-clip-text text-transparent">Online</div>
          </div>
        )}
      </div>
      
      {!collapsed && role && (
        <div className="w-full px-2 py-1 mb-2 bg-[#212B3B] rounded-md text-center">
          <span className="text-xs text-gray-400">
            {role === 'admin' ? 'Administrador' : 
             role === 'mentor' ? 'Mentor' : 'Jogador'}
          </span>
        </div>
      )}
    </div>
  );
};

